import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Building2,
  Calendar,
  Check,
  Copy,
  Download,
  Globe,
  MoreVertical,
  Power,
  RefreshCcw,
  Trash2,
} from 'lucide-react';
import {
  ActionMenu,
  Badge,
  Button,
  IconButton,
  Input,
} from '@ntconsult/aluma-react';
import { useRevokeShare } from '../../hooks/useRevokeShare';
import { useRotateLinkToken } from '../../hooks/useRotateLinkToken';
import { useUpdateShare } from '../../hooks/useUpdateShare';
import type { Share } from '../../types';
import { ConfirmDialog } from '@common/components/ConfirmDialog';

interface LinkCardProps {
  share: Share;
  canManage: boolean;
}

export function LinkCard({ share, canManage }: LinkCardProps) {
  const { t } = useTranslation('shares');
  const [copied, setCopied] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const { mutate: rotateToken, isPending: isRotating } = useRotateLinkToken();
  const { mutate: revokeShare, isPending: isRevoking } = useRevokeShare();
  const { mutate: updateShare } = useUpdateShare();

  const url = share.token
    ? `${window.location.origin}/share/${share.token}`
    : '';

  const handleCopy = async () => {
    if (!url) return;
    await navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const scopeLabel =
    share.linkScope === 'org' ? t('links.scope.org') : t('links.scope.anyone');
  const ScopeIcon = share.linkScope === 'org' ? Building2 : Globe;

  return (
    <li className="flex flex-col gap-2 p-3 border border-border rounded-lg">
        <header className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <ScopeIcon
              className="w-4 h-4 text-foreground-muted shrink-0"
              aria-hidden="true"
            />
            <span className="text-sm font-medium text-foreground truncate">
              {scopeLabel}
            </span>
            {!share.isActive && (
              <Badge tone="danger" variant="soft">
                {t('links.disabled')}
              </Badge>
            )}
          </div>

          {canManage && (
            <ActionMenu
              align="right"
              trigger={
                <IconButton
                  icon={<MoreVertical className="w-4 h-4" />}
                  label={t('links.options')}
                  variant="ghost"
                  size="sm"
                />
              }
            >
              <ActionMenu.Item
                icon={<Power className="w-4 h-4" aria-hidden="true" />}
                onClick={() =>
                  updateShare({
                    id: share.id,
                    data: { isActive: !share.isActive },
                  })
                }
              >
                {share.isActive ? t('links.deactivate') : t('links.activate')}
              </ActionMenu.Item>
              <ActionMenu.Item
                icon={<RefreshCcw className="w-4 h-4" aria-hidden="true" />}
                onClick={() => rotateToken(share.id)}
                disabled={isRotating}
              >
                {t('links.rotate')}
              </ActionMenu.Item>
              <ActionMenu.Separator />
              <ActionMenu.Item
                icon={<Trash2 className="w-4 h-4" aria-hidden="true" />}
                variant="danger"
                onClick={() => setConfirmOpen(true)}
              >
                {t('links.revoke')}
              </ActionMenu.Item>
            </ActionMenu>
          )}
        </header>

        <div className="flex gap-2">
          <Input value={url} readOnly className="flex-1 text-xs" />
          <Button
            variant="secondary"
            size="sm"
            onClick={handleCopy}
            leftIcon={
              copied ? (
                <Check className="w-4 h-4 text-success" aria-hidden="true" />
              ) : (
                <Copy className="w-4 h-4" aria-hidden="true" />
              )
            }
          >
            {copied ? t('links.copied') : t('links.copy')}
          </Button>
        </div>

        <LinkMeta share={share} />

        <ConfirmDialog
          open={confirmOpen}
          pending={isRevoking}
          title={t('links.confirmRevokeTitle')}
          description={t('links.confirmRevokeDescription')}
          confirmLabel={t('links.revoke')}
          onConfirm={() => {
            revokeShare(share.id, { onSuccess: () => setConfirmOpen(false) });
          }}
          onCancel={() => setConfirmOpen(false)}
        />
    </li>
  );
}

function LinkMeta({ share }: { share: Share }) {
  const { t, i18n } = useTranslation('shares');
  const items: React.ReactNode[] = [];

  if (share.expiresAt) {
    items.push(
      <span key="exp" className="inline-flex items-center gap-1">
        <Calendar className="w-3 h-3" aria-hidden="true" />
        {t('links.expiresOn', {
          date: new Date(share.expiresAt).toLocaleDateString(i18n.language),
        })}
      </span>,
    );
  }
  if (share.maxDownloads != null) {
    items.push(
      <span key="dl" className="inline-flex items-center gap-1">
        <Download className="w-3 h-3" aria-hidden="true" />
        {t('links.downloadsUsed', {
          used: share.downloadCount,
          max: share.maxDownloads,
        })}
      </span>,
    );
  }
  if (items.length === 0) {
    items.push(<span key="free">{t('links.unrestricted')}</span>);
  }

  return (
    <div className="flex flex-wrap items-center gap-3 text-xs text-foreground-muted">
      {items}
    </div>
  );
}
